<template>
<!-- WHAT: A components to collect dims-dim model data (Copied from BtDatesModel and combined it with BtDimMetricsModel).
   HISTORY:
   * V251104.1: Fixed a 2-way binding issue by consuming the initial-model prop from BtMetricsModel.
   * V251027.1: Supported 2-way binding: Initialized form fields from v-model.
   * V250709.1: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row class="px-2 pb-2">
      <v-col xs="12" sm="12" md="4" class="py-0">
         <v-autocomplete multiple persistent-hint required deletable-chips small-chips
            ref="dims"
            autocomplete="off"
            class="py-0"
            placeholder="select one or more dimensions..."
            hint="* Dimensions"
            :counter="varcharColumns.length"
            :items="varcharColumns"
            :rules="[rules.requiredArray]"
            v-model="dims"
            @change="dimsChanged"
         ></v-autocomplete>
      </v-col>
      <v-col xs="12" sm="12" md="2" class="pt-1 pb-0">
         <v-select dense persistent-hint hide-selected required
            ref="orderBy"
            autocomplete="off"
            hint="* Order by"
            :items="orderByItems"
            v-model="orderBy"
            @change="orderByChanged"
         ></v-select>
      </v-col>
      <v-col xs="12" sm="12" md="2" class="py-0">
         <v-autocomplete persistent-hint required
            ref="pivotDim"
            autocomplete="off"
            class="py-0"
            placeholder="select the pivot column..."
            hint="* Pivot Column"
            :items="varcharColumns"
            :rules="[rules.required]"
            v-model="pivotDim"
            @change="pivotDimChanged"
         ></v-autocomplete>
      </v-col>
      <v-col xs="12" sm="12" md="4" class="pt-1 pb-0">
         <v-autocomplete multiple persistent-hint required deletable-chips small-chips
            ref="pivotDimIn"
            autocomplete="off"
            class="py-0"
            placeholder="select one or more value..."
            hint="* Pivot Column Values"
            :counter="pivotDimInItems.length"
            :disabled="loadingPivotDimIn"
            :items="pivotDimInItems"
            :loading="loadingPivotDimIn"
            :rules="[rules.requiredArray]"
            v-model="pivotDimIn"
            @change="pivotDimInChanged"
         ></v-autocomplete>
      </v-col>
   </v-row>
   <v-row>
      <v-col cols="12" class="pb-0">
         <bt-metrics-model no-label
            :initial-model="initialModel"
            :max="1"
            :schemas="schemas"
            :query-submitted="querySubmitted"
            v-model="metricsData"
            @form-valid="metricsDataChanged"
         />
      </v-col>
   </v-row>
</v-form>
</template>

<script>
import BtMetricsModel from './BtMetricsModel.vue';
import { isEmpty } from '../mixins/bt-mixin.js';

const NAME = "BtDimsDimModel";
const VERSION = 'V251027.1';

export default {
   name: NAME,

   components: {
      BtMetricsModel
   },

   props: {
      value: {
         type: Object,
         default: () => ({})
      },
      debug: {
         type: Boolean,
         default: false
      },
      initialModel: {
         type: Boolean
      },
      pivotDimInItems: {
         type: Array,
         // default: () => []
      },
      querySubmitted: {
         type: Boolean,
         required: true
      },
      schemas: {
         type: Array,
         required: true,
         // default: () => []
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            requiredArray: value => !!value.length || "Value is required!",
            dateParts: value => value.length === 2 || 'You must select exactly 2 items!'
         },
         orderByItems: [
            { text: 'Ascending', value: 'asc' },
            { text: 'Descending', value: 'desc' }
         ],
         isFormValid: false,
         isMetricsModelValid: true,
         // isFormChanged: false,
         varcharColumns: [],
         dims: [],
         orderBy: 'asc',
         pivotDim: '',
         pivotDimIn: [],
         loadingPivotDimIn: false,
         metricsData: {},
         isInit: false
      }
   },

   computed: {},

   watch: {
      initialModel: {
         immediate: true,
         handler(val) {
            if (val) this.init();
         }
      },

      schemas: {
         immediate: true,
         deep: true,
         async handler(val) {
            // this.alert('in ' + NAME + '/watch: schemas=' + JSON.stringify(val));
            // this.dims = [];
            // this.orderBy = 'asc';
            // this.formChanged();
            if (val?.length) {
               this.varcharColumns = val.filter(col => col.column_type === 'VARCHAR')
                  .map(col => col.column_name);
            } else {
               this.varcharColumns = [];
            }

            // this.isFormChanged = false;

            this.init();

            this.$nextTick(() => {
               // this.formChanged();
               this.$refs.dims?.focus();
            });
         },

         // querySubmitted: {
         //    handler(val) {
         //       if (val) this.isFormChanged = false;
         //    }
         // }
      },

      pivotDimInItems: {
         async handler(items) {
            if (this.isInit) {
               this.isInit = false;
               this.pivotDimIn = this.value.pivotDimIn?.filter(val =>
                  items.some(item => item === val)
               );
            }

            this.loadingPivotDimIn = false;
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },

      init() {
         if (isEmpty(this.value) || !this.varcharColumns?.length) {
            this.dims = [];
            this.orderBy = 'asc';
            this.pivotDim = '';
            this.metricsData = {};
            this.isMetricsModelValid = false;
         } else {
            const { groupByDims, orderBy, pivotDim, metrics, whereClause } = this.value;

            this.pivotDim = this.varcharColumns.includes(pivotDim) ? pivotDim : '';
            if (this.pivotDim) {
               this.isInit = true;
               this.getPivotDimInItems(pivotDim);
            }

            this.dims = groupByDims.filter(dim =>
               this.varcharColumns.some(col => col === dim)
            );
            
            const orderByParts = orderBy.split(',')[0].split(' ');
            this.orderBy = orderByParts[orderByParts.length - 1];

            this.metricsData = {
               metrics: [...metrics],
               ...(whereClause && { whereClause: whereClause })
            }
            
            this.isMetricsModelValid = true;
         }
      },

      dimsChanged() {
         this.formChanged();
      },

      orderByChanged() {
         this.formChanged();
      },
      
      pivotDimChanged(val) {
         // this.loadingPivotDimIn = true;
         // this.$emit('pivot-dim-change', val);
         this.pivotDimIn = [];
         this.getPivotDimInItems(val);
         this.formChanged();
         this.$nextTick(() => {
            this.$refs.pivotDimIn?.focus();
         });
      },

      getPivotDimInItems(pivotDim) {
         this.loadingPivotDimIn = true;
         this.$emit('pivot-dim-change', pivotDim);
      },

      pivotDimInChanged() {
         this.formChanged();
      },

      metricsDataChanged(val) {
         // alert('in metricsDataChanged(): val=' + val);
         this.isMetricsModelValid = val;
         this.formChanged();
      },

      formChanged() {
         let emitVal = false;
         this.isFormValid = this.$refs.form?.validate();
         if (this.isFormValid && this.isMetricsModelValid) {
            const orderBy = [];
            this.dims.forEach(item => {
               orderBy.push(`${item} ${this.orderBy}`);
            });
            const modelData = {
               groupByDims: this.dims,
               orderBy: orderBy.join(','),
               pivotDim: this.pivotDim,
               pivotDimIn: this.pivotDimIn,
               pivotDimQuote: true,
               ...this.metricsData
            };

            this.$emit('input', modelData);
            emitVal = true;
         }

         this.$emit('form-valid', emitVal);
      }
   },

   mounted() {
      // alert(`in ${NAME}.mounted(): `);
      if (this.initialModel === undefined)
         this.init();
   }
}
</script>

<style scoped>
</style>